/**
 * Modules Section Component
 *
 * Displays user's enrolled modules grouped by time status (current/upcoming/past)
 * Smart component that manages module grouping
 */

import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactModuleDetail } from '../../core/models';
import { ModuleListComponent } from './module-list.component';
import { TranslatePipe } from '../../core/features/translations';

@Component({
  selector: 'app-modules-section',
  standalone: true,
  imports: [
    CommonModule,
    ModuleListComponent,
    TranslatePipe,
  ],
  template: `
    <div class="flex flex-col gap-8 md:gap-6">
      @if (totalModulesCount() === 0) {
        <div class="flex flex-col items-center justify-center p-12 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 transition-colors hover:border-gray-300">
          <i class="pi pi-book text-gray-300 text-6xl mb-4"></i>
          <p class="text-gray-500 font-semibold m-0 text-base leading-relaxed">{{ 'portal.degrees.modules.empty' | translate }}</p>
        </div>
      } @else {
        <!-- Current Modules -->
        @if (currentModulesCount() > 0) {
          <div class="flex flex-col gap-4">
            <h3 class="flex items-center gap-2 text-lg font-bold text-gray-900 m-0 pb-2 border-b border-gray-100">
              <i class="pi pi-play-circle text-green-600 text-xl"></i>
              {{ 'portal.degrees.modules.current' | translate }} ({{ currentModulesCount() }})
            </h3>
            <app-module-list [modules]="currentModules()" cardClass="current" />
          </div>
        }

        <!-- Upcoming Modules -->
        @if (upcomingModulesCount() > 0) {
          <div class="flex flex-col gap-4">
            <h3 class="flex items-center gap-2 text-lg font-bold text-gray-900 m-0 pb-2 border-b border-gray-100">
              <i class="pi pi-calendar-plus text-blue-600 text-xl"></i>
              {{ 'portal.degrees.modules.upcoming' | translate }} ({{ upcomingModulesCount() }})
            </h3>
            <app-module-list [modules]="upcomingModules()" cardClass="upcoming" />
          </div>
        }

        <!-- Past Modules -->
        @if (pastModulesCount() > 0) {
          <div class="flex flex-col gap-4">
            <h3 class="flex items-center gap-2 text-lg font-bold text-gray-900 m-0 pb-2 border-b border-gray-100">
              <i class="pi pi-history text-gray-400 text-xl"></i>
              {{ 'portal.degrees.modules.past' | translate }} ({{ pastModulesCount() }})
            </h3>
            <app-module-list [modules]="pastModules()" cardClass="past" />
          </div>
        }
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModulesSectionComponent {
  // Inputs
  currentModules = input<ContactModuleDetail[]>([]);
  upcomingModules = input<ContactModuleDetail[]>([]);
  pastModules = input<ContactModuleDetail[]>([]);

  // Computed values
  currentModulesCount = computed(() => this.currentModules().length);
  upcomingModulesCount = computed(() => this.upcomingModules().length);
  pastModulesCount = computed(() => this.pastModules().length);
  totalModulesCount = computed(
    () => this.currentModulesCount() + this.upcomingModulesCount() + this.pastModulesCount()
  );
}
